import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import DashboardLayout from "@/layouts/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Suppliers from "@/pages/Suppliers";
import Customers from "@/pages/Customers";
import Vehicles from "@/pages/Vehicles";
import Transactions from "@/pages/Transactions";
import Reports from "@/pages/Reports";
import Staff from "@/pages/Staff";
import Hours from "@/pages/Hours";
import Accounting from "@/pages/Accounting";
import Attention from "@/pages/Attention";
import Pricing from "@/pages/Pricing";
import Sourcing from "@/pages/Sourcing";
import Returns from "@/pages/Returns";
import GoLive from "@/pages/GoLive";
import LaborTimes from "@/pages/LaborTimes";
import Quotes from "@/pages/Quotes";
import Rentals from "@/pages/Rentals";
import Kiosk from "@/pages/Kiosk";
import Badge from "@/pages/Badge";
import Settings from "@/pages/Settings";
import Procurement from "@/pages/Procurement";
import AuditLog from "@/pages/AuditLog";
import Invoices from "@/pages/Invoices";
import Repairs from "@/pages/Repairs";
import MechanicCard from "@/pages/MechanicCard";
import CashRegister from "@/pages/CashRegister";
import Reminders from "@/pages/Reminders";
import Accounts from "@/pages/Accounts";
import Bank from "@/pages/Bank";
import CalendarPage from "@/pages/Calendar";
import Workboard from "@/pages/Workboard";
import CarPassport from "@/pages/CarPassport";
import BayBoard from "@/pages/BayBoard";
import DeliveryScan from "@/pages/DeliveryScan";
import PasswordSetup from "@/pages/PasswordSetup";
import SuperAdmin from "@/pages/SuperAdmin";
import MyProfile from "@/pages/MyProfile";
import EmailLogs from "@/pages/EmailLogs";
import PayInvoice from "@/pages/PayInvoice";
import InspectionReport from "@/pages/InspectionReport";
import PermGate from "@/components/PermGate";
import "@/App.css";

function ProtectedShell() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground font-mono text-sm">Loading garage...</div>
      </div>
    );
  }
  // After logout we want the crisp /login page — NOT the marketing landing
  // (which briefly flashed as "the old design" during a full reload).
  if (!user) return <Navigate to="/login" replace />;
  return <DashboardLayout><Outlet /></DashboardLayout>;
}

function OwnerRoute({ children }) {
  const { user, firstAllowedPath } = useAuth();
  if (!user) return null;
  // Super-admins can only reach owner-only pages while impersonating a
  // tenant.  Outside impersonation they get bounced to /super-admin so
  // they can't accidentally write to /staff or /settings globally.
  if (user.role === "super_admin" && !user.impersonating) {
    return <Navigate to="/super-admin" replace />;
  }
  if (user.role !== "owner" && user.role !== "super_admin") {
    return <Navigate to={firstAllowedPath()} replace />;
  }
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, firstAllowedPath } = useAuth();
  if (user && user.role !== "super_admin") {
    return <Navigate to={firstAllowedPath()} replace />;
  }
  return children;
}

function LandingHome() {
  const { user, ready, firstAllowedPath } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to={firstAllowedPath()} replace />;
  return <Navigate to="/login" replace />;
}

function DashboardFallback() {
  // Owner → real Dashboard.  Staff without reports.view → their first allowed section.
  // Super-admins land on /super-admin unless they are actively impersonating.
  const { user, ready, hasPermission, firstAllowedPath } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin" && !user.impersonating) {
    return <Navigate to="/super-admin" replace />;
  }
  if (user.role === "owner" || hasPermission("reports.view")) {
    return <Dashboard />;
  }
  return <Navigate to={firstAllowedPath()} replace />;
}

/* Bridges React Router's imperative `navigate` into the AuthContext so
   `logout()` (and future flows) can SPA-navigate instead of doing a
   `window.location.href = "/login"` full reload — which flashed the
   Landing page ("old design") on the way to the Login page. */
function NavigatorBinder() {
  const nav = useNavigate();
  const { _bindNavigate } = useAuth();
  useEffect(() => { _bindNavigate?.(nav); }, [_bindNavigate, nav]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <NavigatorBinder />
          <LanguageProvider>
          <Routes>
            <Route path="/" element={<LandingHome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/passport/:token" element={<CarPassport />} />
            <Route path="/setup-password/:token" element={<PasswordSetup />} />
            <Route path="/pay/:token" element={<PayInvoice />} />
            <Route path="/rapport/:token" element={<InspectionReport />} />
            <Route path="/kiosk" element={<Kiosk />} />
            <Route path="/badge/:token" element={<Badge />} />
            <Route element={<ProtectedShell />}>
              <Route path="/dashboard" element={<DashboardFallback />} />
              <Route path="/inventory" element={<PermGate perm="inventory.view"><Inventory /></PermGate>} />
              <Route path="/transactions" element={<PermGate perm="inventory.view"><Transactions /></PermGate>} />
              <Route path="/suppliers" element={<PermGate perm="suppliers.view"><Suppliers /></PermGate>} />
              <Route path="/customers" element={<PermGate perm="customers.view"><Customers /></PermGate>} />
              <Route path="/vehicles" element={<PermGate perm="customers.view"><Vehicles /></PermGate>} />
              <Route path="/reports" element={<PermGate perm="reports.view"><Reports /></PermGate>} />
              <Route path="/repairs" element={<PermGate perm="repairs.view"><Repairs /></PermGate>} />
              <Route path="/labor-times" element={<PermGate perm="repairs.view"><LaborTimes /></PermGate>} />
              <Route path="/quotes" element={<PermGate perm="repairs.view"><Quotes /></PermGate>} />
              <Route path="/rentals" element={<PermGate perm="repairs.view"><Rentals /></PermGate>} />
              <Route path="/repairs/:id/mechanic" element={<PermGate perm="repairs.view"><MechanicCard /></PermGate>} />
              <Route path="/reminders" element={<Navigate to="/vehicles?tab=reminders" replace />} />
              <Route path="/cash-register" element={<PermGate perm="cash.view"><CashRegister /></PermGate>} />
              <Route path="/accounts" element={<Navigate to="/bank?tab=accounts" replace />} />
              <Route path="/bank" element={<Bank />} />
              <Route path="/calendar" element={<PermGate perm="calendar.view"><CalendarPage /></PermGate>} />
              <Route path="/workboard" element={<PermGate perm="calendar.view"><Workboard /></PermGate>} />
              <Route path="/bay-board" element={<PermGate perm="calendar.view"><BayBoard /></PermGate>} />
              <Route path="/delivery-scan" element={<PermGate perm="delivery_scan.use"><DeliveryScan /></PermGate>} />
              <Route path="/invoices" element={<PermGate perm="invoices.view"><Invoices /></PermGate>} />
              <Route path="/purchase-orders" element={<PermGate perm="purchase_orders.view"><Procurement /></PermGate>} />
              <Route path="/audit-log" element={<PermGate perm="audit.view"><AuditLog /></PermGate>} />
              <Route path="/staff" element={<OwnerRoute><Staff /></OwnerRoute>} />
              <Route path="/hours" element={<PermGate perm="hours.view"><Hours /></PermGate>} />
              <Route path="/accounting" element={<OwnerRoute><Accounting /></OwnerRoute>} />
              <Route path="/attention" element={<PermGate perm="reports.view"><Attention /></PermGate>} />
              <Route path="/pricing" element={<PermGate perm="prices.inventory"><Pricing /></PermGate>} />
              <Route path="/sourcing" element={<PermGate perm="prices.inventory"><Sourcing /></PermGate>} />
              <Route path="/returns" element={<PermGate perm="purchase_orders.view"><Returns /></PermGate>} />
              <Route path="/golive" element={<OwnerRoute><GoLive /></OwnerRoute>} />
              <Route path="/settings" element={<OwnerRoute><Settings /></OwnerRoute>} />
              <Route path="/super-admin" element={<SuperAdminRoute><SuperAdmin /></SuperAdminRoute>} />
              <Route path="/my-profile" element={<MyProfile />} />
              <Route path="/email-logs" element={<SuperAdminRoute><EmailLogs /></SuperAdminRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </LanguageProvider>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
